// Story E6.3.2 — dropdown manual de status no Header.
//
// Compõe com PresenceContext: lê selfStatus, chama setStatus(novo), que grava a
// escolha no servidor (vale até a pessoa mudar, em todas as abas e aparelhos) e
// dispara heartbeat. Backend broadcasta pra company se o status mudou.
// "Online" = voltar ao automático. Falha ao gravar mostra um aviso (toast do shadcn).
//
// Renderiza compacto: bolinha + label curto (online/away/busy). Sem
// "offline" — user não escolhe ficar offline (basta fechar a aba).

import { ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { usePresenceSafe } from "@/contexts/PresenceContext";
// Aviso pelo `use-toast` do shadcn, que este dropdown já usava (o Toaster do sonner só
// entrou no App no passo 9, de 06/10); migrar para o sonner é opcional.
import { toast as avisar } from "@/hooks/use-toast";
import type { SettableStatus } from "@/services/presenceService";

const OPTIONS: Array<{
  value: SettableStatus;
  label: string;
  desc: string;
  dotClass: string;
}> = [
  {
    value: "online",
    label: "Online",
    desc: "Automático: fica Ausente quando você sai da aba",
    dotClass: "bg-green-500",
  },
  {
    value: "away",
    label: "Ausente",
    desc: "Longe do teclado — fixo até eu mudar",
    dotClass: "bg-yellow-500",
  },
  {
    value: "busy",
    label: "Ocupado",
    desc: "Não me incomode — fixo até eu mudar",
    dotClass: "bg-red-500",
  },
];

const LABEL_BY_STATUS: Record<string, string> = {
  online: "Online",
  away: "Ausente",
  busy: "Ocupado",
  offline: "Offline",
};

const DOT_BY_STATUS: Record<string, string> = {
  online: "bg-green-500",
  away: "bg-yellow-500",
  busy: "bg-red-500",
  offline: "bg-gray-400",
};

interface Props {
  compact?: boolean;
}

export function StatusDropdown({ compact = false }: Props) {
  const { selfStatus, setStatus, wsConnected } = usePresenceSafe();

  const escolher = async (status: SettableStatus) => {
    try {
      await setStatus(status);
    } catch {
      avisar({
        title: "Não foi possível mudar o status",
        description: "Tente de novo em instantes.",
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="status-dropdown-trigger"
        className={cn(
          // h-9 + min-w-[44px]: alvo de toque adequado e altura consistente
          // com os demais botões do header (ThemeToggle/avatar = h-9).
          "inline-flex h-9 min-w-[44px] items-center gap-1.5 rounded-md px-2 text-sm",
          "hover:bg-accent hover:text-accent-foreground transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-ring",
          !wsConnected && "opacity-60"
        )}
        aria-label="Alterar status de presença"
      >
        <span
          data-testid="status-dropdown-self-dot"
          className={cn("inline-block h-2.5 w-2.5 rounded-full", DOT_BY_STATUS[selfStatus])}
        />
        {!compact && <span className="hidden sm:inline">{LABEL_BY_STATUS[selfStatus]}</span>}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {wsConnected ? "Definir status manualmente" : "Conectando ao servidor…"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            data-testid={`status-option-${opt.value}`}
            onClick={() => escolher(opt.value)}
            className="flex items-start gap-2"
          >
            <span
              className={cn(
                "mt-1 inline-block h-2.5 w-2.5 rounded-full flex-shrink-0",
                opt.dotClass
              )}
            />
            <div className="flex flex-col">
              <span className="text-sm font-medium">{opt.label}</span>
              <span className="text-xs text-muted-foreground">{opt.desc}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
